/**
 * 🪶 **형제보다 유난히 큰 그림** — 판정을 여기 한 곳에.
 *
 * ## 왜 생겼나 (2026-09-06)
 * `hero.base.explorer_a.side` 만 **1536×512**(프레임 512px)인데 앞·뒤는 **512×128**(프레임 128px)이다.
 * 화면에서는 48~64px 로 그려지니 8배가 통째로 버려지고, `/world` 첫 화면에서 그 한 파일이
 * **695ms** 를 먹었다.
 *
 * 원인은 「누가 실수했다」가 아니라 **같은 것을 만드는 길이 둘**이라는 데 있었다:
 *  · `scripts/slice-walk-sheet.cjs` → 프레임 **128px**
 *  · 관리자 [🎞️ 움직임] → `apps/admin/src/lib/strip.ts` 의 `FRAME` = **512px**
 * 어느 쪽으로 만들었느냐에 따라 8배가 갈리는데 **아무 경고도 없었다.**
 *
 * ## 무엇을 판정하나
 * 절대 크기로 재지 않는다 — 책 스캔본은 원래 크고 배경도 크다. **형제와 견준다.**
 * 걷기 띠는 가로로 이어 붙인 정사각 프레임이라 **높이 = 프레임 한 변**이다.
 */

/** 같은 계열 — 키의 마지막 조각만 다른 것들(`hero.base.explorer_a.side` → `hero.base.explorer_a`). */
export function artFamilyOf(key: string): string {
  return key.split(".").slice(0, -1).join(".");
}

/** 형제 중간값의 몇 배부터 「유난히」로 볼까. 2배면 픽셀이 4배다. */
export const ODD_FRAME_TIMES = 2;

/**
 * 🔴 **형제들이 입을 모은 크기** — 중간값이 아니라 **가장 많은 값**이다.
 *
 * 처음엔 중간값을 썼다. 형제가 둘인데 서로 다르면(512·128) 중간값이 **320** 이 된다 —
 * **아무도 아닌 크기다.** 실제로 그렇게 나와서, 멀쩡한 128px 띠를 320px 로 키우라는
 * 단추가 세 개나 떴다(관리자에서 눌러 보고 알았다. 눌렀으면 아이 화면이 더 무거워졌다).
 *
 * 그래서 **한 크기가 뚜렷이 많을 때만** 답한다. 형제끼리 갈려 있으면 어느 쪽이 맞는지
 * 우리는 모른다 — 그때는 아무 말도 안 하는 것이 맞다.
 */
function agreedSize(ns: number[]): number | null {
  const count = new Map<number, number>();
  for (const n of ns) count.set(n, (count.get(n) ?? 0) + 1);
  const ranked = [...count.entries()].sort((a, b) => b[1] - a[1]);
  const top = ranked[0];
  if (!top || top[1] < 2) return null; // 같은 크기가 둘은 있어야 「형제들의 크기」다
  if (ranked[1] && ranked[1][1] === top[1]) return null; // 반반이면 모른다
  return top[0];
}

/**
 * 🪶 **형제들이 입을 모은 칸 크기**(px) — 모르면 `null`.
 *
 * 관리자 [🎞️ 움직임]이 띠를 **새로 굽을 때**(게시·저장·내보내기) 이 크기로 굽는다(2026-09-14).
 * 🔴 전에는 칸 크기를 안 넘겨 기본 512px 로 구웠다 — 128px 걷기를 편집해 올리자 **8배(127KB)** 로 부풀었다.
 * 규칙은 「형제와 같은 크기로」 단추(`oddFrameSize`)와 같다: 둘 이상이 같은 크기로 모여 있어야 답한다.
 */
export function familyFrameSize(siblings: number[]): number | null {
  return agreedSize(siblings.filter((n) => Number.isFinite(n) && n > 0));
}

/**
 * 이 그림의 프레임 크기가 형제들과 어긋나 있나 — 맞춰야 할 크기(px)를 돌려준다. 아니면 `null`.
 *
 * 🔴 형제가 **둘 이상**이어야 하고, 그 형제들이 **같은 크기로 모여 있어야** 한다.
 *    하나뿐이거나 둘이 서로 다르면 어느 쪽이 맞는지 알 수 없다 —
 *    그때 고치라고 하면 멀쩡한 것을 반반 확률로 망가뜨린다(실제로 그럴 뻔했다).
 * 🔴 **작은 쪽도 잡는다.** 형제보다 훨씬 작으면 아이 화면에서 뭉개진다 — 반대 방향의 같은 사고다.
 */
export function oddFrameSize(mine: number, siblings: number[]): number | null {
  const others = siblings.filter((n) => Number.isFinite(n) && n > 0);
  if (!Number.isFinite(mine) || mine <= 0 || others.length < 2) return null;
  const agreed = agreedSize(others);
  if (!agreed || agreed <= 0) return null;
  const times = mine >= agreed ? mine / agreed : agreed / mine;
  return times >= ODD_FRAME_TIMES ? agreed : null;
}
